import React, { memo, useCallback, useMemo } from 'react'
import { ControlledCheckbox } from 'transfix-form/components'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { TCarrierLoadPreferencesSubmitHandler } from '../../types'
import {
  IUseExcludedCommoditiesFormProps,
  useExcludedCommoditiesForm,
} from './hooks/useExcludedCommoditiesForm'
import { TExcludedCommoditiesSchema } from './schema/ExcludedCommoditiesSchema'
import { EXCLUDED_COMMODITIES_LABELS, IExcludedCommodities } from './types'

export interface IExcludedCommoditiesModalProps extends Pick<IFormModalProps, 'onClose'> {
  /**
   * @default {}
   */
  defaultValues?: IUseExcludedCommoditiesFormProps['defaultValues']
  loading: boolean
  onSubmit: TCarrierLoadPreferencesSubmitHandler<TExcludedCommoditiesSchema>
}

const ExcludedCommoditiesModal = memo<IExcludedCommoditiesModalProps>(
  ({ defaultValues = {}, loading, onClose, onSubmit }) => {
    const { control, handleSubmit } = useExcludedCommoditiesForm({ defaultValues })

    const submitHandler = useCallback(
      async (formValues: TExcludedCommoditiesSchema) => {
        const result = await onSubmit(formValues)
        if (result) {
          onClose()
        }
      },
      [onClose, onSubmit]
    )

    const actions: IFormModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
          disabled: loading,
        },
        {
          label: 'Update',
          type: 'confirm',
          action: handleSubmit(submitHandler),
          disabled: loading,
        },
      ],
      [handleSubmit, loading, onClose, submitHandler]
    )

    return (
      <FormModal title='Excluded commodities' actions={actions} onClose={onClose} open>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={1.5}>
              <Typography component='div'>
                Are there any commodities that you would prefer to not to run?
              </Typography>
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='alcohol'
              CheckboxProps={{ label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.ALCOHOL] }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='garment'
              CheckboxProps={{ label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.GARMENT] }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='tobacco'
              CheckboxProps={{ label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.TOBACCO] }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='metal'
              CheckboxProps={{ label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.METAL] }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='jewelry'
              CheckboxProps={{ label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.JEWELRY] }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='meat'
              CheckboxProps={{ label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.MEAT] }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='electronic'
              CheckboxProps={{
                label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.ELECTRONIC],
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='babyFood'
              CheckboxProps={{
                label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.BABY_FOOD],
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='seafood'
              CheckboxProps={{
                label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.SEAFOOD],
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='cosmetic'
              CheckboxProps={{
                label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.COSMETIC],
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='medication'
              CheckboxProps={{
                label: EXCLUDED_COMMODITIES_LABELS[IExcludedCommodities.MEDICATION],
              }}
            />
          </Grid>
        </Grid>
      </FormModal>
    )
  }
)

export default ExcludedCommoditiesModal
